/**
 * Telegram WebApp (скрипт telegram-web-app.js подключён в index.html).
 * Вне Telegram (локальная разработка) initData берётся из #tgWebAppData=… — см. pnpm dev:initdata.
 */
interface TgWebApp {
  initData: string;
  initDataUnsafe: { user?: { id: number; first_name?: string; language_code?: string }; start_param?: string };
  ready(): void;
  expand(): void;
  close(): void;
  setHeaderColor?(color: string): void;
  setBackgroundColor?(color: string): void;
  BackButton: { show(): void; hide(): void; onClick(cb: () => void): void; offClick(cb: () => void): void };
  HapticFeedback?: { impactOccurred(s: 'light' | 'medium' | 'heavy'): void; notificationOccurred(s: 'success' | 'error' | 'warning'): void };
  showConfirm?(message: string, cb: (ok: boolean) => void): void;
  openLink?(url: string): void;
  openTelegramLink?(url: string): void;
  showScanQrPopup?(params: { text?: string }, cb: (text: string) => boolean | void): void;
  closeScanQrPopup?(): void;
  platform?: string;
}

declare global {
  interface Window {
    Telegram?: { WebApp?: TgWebApp };
  }
}

export const tg = (): TgWebApp | undefined => (typeof window !== 'undefined' ? window.Telegram?.WebApp : undefined);

/** Сканер QR Telegram (только внутри Telegram 6.4+); null — сканера нет, например в браузере */
export function scanQr(text: string): Promise<string> | null {
  const app = tg();
  if (!app?.showScanQrPopup || !app.initData) return null;
  return new Promise((resolve) => {
    app.showScanQrPopup!({ text }, (value) => {
      resolve(value);
      return true;
    });
  });
}

/** Ссылка t.me внутри Telegram открывает чат без выхода из Mini App; в браузере — новая вкладка */
export function openTelegram(url: string) {
  const app = tg();
  if (app?.openTelegramLink && app.initData) app.openTelegramLink(url);
  else window.open(url, '_blank', 'noopener');
}

/** Страница оплаты: внутри Telegram — t.me-ссылка без выхода из приложения, иначе браузер */
export function openPayment(links: { paymentUrl: string; webappUrl: string | null }) {
  const app = tg();
  if (app?.initData && links.webappUrl && app.openTelegramLink && /^https:\/\/(t|telegram)\.me\//.test(links.webappUrl)) app.openTelegramLink(links.webappUrl);
  else if (app?.initData && app.openLink) app.openLink(links.paymentUrl);
  else window.open(links.paymentUrl, '_blank', 'noopener');
}

/** Деньги в € / ₽: суммы в центах (€) и рублях */
export function formatMoney(locale: string, value: number, currency: 'EUR' | 'RUB') {
  const n = currency === 'EUR' ? value / 100 : value;
  return new Intl.NumberFormat(locale === 'zh' ? 'zh-CN' : locale, { style: 'currency', currency, minimumFractionDigits: Number.isInteger(n) ? 0 : 2, maximumFractionDigits: currency === 'EUR' ? 2 : 0 }).format(n);
}

const DEV_KEY = 'barev.dev.initData';

/** initData для заголовка Authorization: tma … */
export function getInitData(): string {
  const real = tg()?.initData;
  if (real) {
    // telegram-web-app.js уже прочитал параметры запуска — убираем их из адреса
    if (window.location.hash.includes('tgWebAppData')) window.history.replaceState(null, '', window.location.pathname + window.location.search + '#/');
    return real;
  }
  // разработка: #tgWebAppData=<urlencoded initData>, запоминаем на вкладку
  const m = /tgWebAppData=([^&]+)/.exec(window.location.hash);
  if (m) {
    const data = decodeURIComponent(m[1]!);
    try {
      sessionStorage.setItem(DEV_KEY, data);
    } catch {
      /* приватный режим */
    }
    window.history.replaceState(null, '', window.location.pathname + '#/');
    return data;
  }
  try {
    return sessionStorage.getItem(DEV_KEY) ?? '';
  } catch {
    return '';
  }
}

/** Маршрут из ?r=/orders/123 — так ведут кнопки в уведомлениях (hash занят параметрами Telegram) */
function applyStartRoute() {
  const params = new URLSearchParams(window.location.search);
  const r = params.get('r');
  if (!r?.startsWith('/')) return;
  params.delete('r');
  const qs = params.size ? `?${params}` : '';
  window.history.replaceState(null, '', `${window.location.pathname}${qs}#${r}`);
}

export function initTelegram(bg = '#FBF6EE') {
  installErrorReporting();
  getInitData();
  applyStartRoute();
  const app = tg();
  if (!app) return;
  app.ready();
  app.expand();
  app.setHeaderColor?.(bg);
  app.setBackgroundColor?.(bg);
}

/** Язык пользователя Telegram — пока API не ответило */
export function telegramLanguage(): string | undefined {
  const fromTg = tg()?.initDataUnsafe.user?.language_code;
  if (fromTg) return fromTg;
  try {
    const user = new URLSearchParams(getInitData()).get('user');
    return user ? (JSON.parse(user) as { language_code?: string }).language_code : undefined;
  } catch {
    return undefined;
  }
}

/** Виброотклик. Не во всех клиентах Telegram он поддерживается — ошибки глушим, это не повод ломать сценарий */
function safe(fn: () => void) {
  try {
    fn();
  } catch {
    /* метод не поддерживается этой версией Telegram */
  }
}
export const haptic = {
  tap: () => safe(() => tg()?.HapticFeedback?.impactOccurred('light')),
  ok: () => safe(() => tg()?.HapticFeedback?.notificationOccurred('success')),
  error: () => safe(() => tg()?.HapticFeedback?.notificationOccurred('error')),
};

/** Подтверждение: нативное в Telegram, window.confirm в браузере */
export function confirmDialog(message: string): Promise<boolean> {
  const app = tg();
  if (app?.showConfirm && app.initData) {
    try {
      return new Promise((resolve) => app.showConfirm!(message, resolve));
    } catch {
      /* старая версия Telegram — обычный confirm */
    }
  }
  return Promise.resolve(window.confirm(message));
}

/**
 * Сообщить серверу об ошибке в браузере (в логе API: client error). Без персональных данных:
 * текст ошибки, первые строки стека, адрес экрана и версия Telegram.
 */
export function reportClientError(err: unknown, where = 'unknown') {
  try {
    const e = err as { message?: string; stack?: string; data?: unknown };
    if (e?.data) return; // ошибки API и так видны в логах сервера
    const body = JSON.stringify({
      where,
      message: String(e?.message ?? err).slice(0, 300),
      stack: String(e?.stack ?? '').split('\n').slice(0, 6).join('\n').slice(0, 1200),
      path: window.location.hash.split('?')[0]?.slice(0, 80),
      tg: tg()?.platform ? `${tg()!.platform} ${(tg() as unknown as { version?: string }).version ?? ''}` : 'browser',
    });
    navigator.sendBeacon?.('/client-log', new Blob([body], { type: 'application/json' }));
  } catch {
    /* не мешаем пользователю */
  }
}

let installed = false;
/** Перехват необработанных ошибок страницы */
export function installErrorReporting() {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  window.addEventListener('error', (e) => reportClientError(e.error ?? e.message, 'window.onerror'));
  window.addEventListener('unhandledrejection', (e) => reportClientError(e.reason, 'unhandledrejection'));
}
